import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAddressLatinCharacterValidation } from '@/shared/hooks/use-address-latin-character-validation'
import { NON_EU_COUNTRIES } from '@/shared/config/countries'

export type OrganizationAddressFormColumnProps = {
  isLocal: boolean
  countryCode: string
  street?: string
  streetNumber?: string
  street2?: string
  postCode?: string
  poBox?: string
  city: string
  subDivision?: string
  latinErrorMessage: string
  onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
  inputIdPrefix?: string
  showRequiredIndicators?: boolean
}

export function OrganizationAddressFormColumn({
  isLocal,
  countryCode,
  street,
  streetNumber,
  street2,
  postCode,
  poBox,
  city,
  subDivision,
  latinErrorMessage,
  onChange,
  inputIdPrefix = '',
  showRequiredIndicators = true,
}: OrganizationAddressFormColumnProps) {
  const { t } = useTranslation('common')
  const { t: tSettings } = useTranslation('settings')
  const errors = useAddressLatinCharacterValidation(
    isLocal
      ? {}
      : {
          street,
          street2: street2 || '',
          streetNumber,
          postCode: postCode || '',
          poBox,
          city,
          subDivision,
        },
    latinErrorMessage
  )

  const flagCode = isLocal ? countryCode : 'gb'
  const postCodeRequired = NON_EU_COUNTRIES.find(
    (country) => country.value === countryCode
  )?.isPostCodeRequired

  const fieldId = (base: string) => `${inputIdPrefix}${base}`

  return (
    <div>
      <p className="mb-2 text-sm">
        {tSettings('organizationDetails.details.address')}{' '}
        <ReactCountryFlag countryCode={flagCode} svg />
      </p>
      <div className="space-y-4 rounded-lg border p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor={fieldId('street')}>{t('inputFields.street.label')}</Label>
            <Input
              id={fieldId('street')}
              value={street ?? ''}
              onChange={onChange}
              aria-invalid={!isLocal && !!errors.street}
            />
            {!isLocal && errors.street ? (
              <p className="text-sm text-destructive">{errors.street}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor={fieldId('streetNumber')}>
              {t('inputFields.streetNumber.label')}
            </Label>
            <Input
              id={fieldId('streetNumber')}
              value={streetNumber ?? ''}
              onChange={onChange}
              aria-invalid={!isLocal && !!errors.streetNumber}
            />
            {!isLocal && errors.streetNumber ? (
              <p className="text-sm text-destructive">{errors.streetNumber}</p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={fieldId('street2')}>{t('inputFields.street2.label')}</Label>
          <Input
            id={fieldId('street2')}
            value={street2 ?? ''}
            onChange={onChange}
            aria-invalid={!isLocal && !!errors.street2}
          />
          {!isLocal && errors.street2 ? (
            <p className="text-sm text-destructive">{errors.street2}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor={fieldId('postCode')}>
              {t('inputFields.postCode.label')}
              {showRequiredIndicators && postCodeRequired ? ' *' : ''}
            </Label>
            <Input
              id={fieldId('postCode')}
              value={postCode ?? ''}
              onChange={onChange}
              aria-invalid={!isLocal && !!errors.postCode}
            />
            {!isLocal && errors.postCode ? (
              <p className="text-sm text-destructive">{errors.postCode}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor={fieldId('city')}>
              {t('inputFields.city.label')}
              {showRequiredIndicators ? ' *' : ''}
            </Label>
            <Input
              id={fieldId('city')}
              value={city}
              onChange={onChange}
              aria-invalid={!isLocal && !!errors.city}
            />
            {!isLocal && errors.city ? (
              <p className="text-sm text-destructive">{errors.city}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor={fieldId('poBox')}>{t('inputFields.poBox.label')}</Label>
            <Input
              id={fieldId('poBox')}
              value={poBox ?? ''}
              onChange={onChange}
              aria-invalid={!isLocal && !!errors.poBox}
            />
            {!isLocal && errors.poBox ? (
              <p className="text-sm text-destructive">{errors.poBox}</p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={fieldId('subDivision')}>
            {t('inputFields.subDivision.label')}
          </Label>
          <Input
            id={fieldId('subDivision')}
            value={subDivision ?? ''}
            onChange={onChange}
            aria-invalid={!isLocal && !!errors.subDivision}
          />
          {!isLocal && errors.subDivision ? (
            <p className="text-sm text-destructive">{errors.subDivision}</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
